import { useCallback, useEffect, useId, useRef, useState } from "react";
import { ApiError } from "../../../lib/http/errors";
import { Button } from "../../../components/ui/Button";
import { Empty, ErrorBanner } from "../../../components/Shell";
import { hasMessage, useT } from "../../../i18n";
import type { OpsApi } from "../api/opsApi";
import type { AssetVersion, Rotation, ServiceKey } from "../types";
import { fmtDate } from "./ConfigTab";

/**
 * FS-15-05 자산 버전·서비스 키 — SCR v0.6 sfs-15-05 · SR-OPS-02 · SR-SEC-04.
 *  - 자산 버전 표(라이브러리 · 버전 · 체크섬 · 게시 · 상태) + 불변 캐시 안내. 업로드·폐기는 CI(계약에 자산 폐기 API 없음)
 *  - 서비스 간 서명 키 표(키 ID · 서비스 · 만든 날 · 회전 · 상태) · 회전(→ 새 비밀은 **한 번만** 보인다) · 폐기(두 번 눌러 확인)
 * 비밀은 화면 상태에만 잠깐 있고 저장하지 않는다(닫으면 사라짐 — 다시 볼 수 없음).
 */
export const shortHash = (h: string) => {
  const v = h.replace(/^sha256:/, "");
  return v.length > 4 ? `${v.slice(0, 4)}…` : v;
};

const fmtDateTime = (iso: string | null) =>
  iso ? `${iso.slice(0, 10)} ${iso.slice(11, 16)}` : "—";

export function AssetsTab({
  api,
  onLatest,
}: {
  api: OpsApi;
  onLatest: (label: string | null) => void;
}) {
  const t = useT();
  const id = useId();
  const [assets, setAssets] = useState<AssetVersion[] | null>(null);
  const [keys, setKeys] = useState<ServiceKey[] | null>(null);
  const [secret, setSecret] = useState<Rotation | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirm, setConfirm] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const secretRef = useRef<HTMLDivElement>(null);

  const loadKeys = useCallback(async () => setKeys(await api.serviceKeys()), [api]);
  useEffect(() => {
    let off = false;
    Promise.all([api.assets(), api.serviceKeys()])
      .then(([a, k]) => {
        if (off) return;
        setAssets(a);
        setKeys(k);
        const b = a.find((x) => x.library === "brick-parts" && x.status === "latest") ?? a[0];
        onLatest(b ? `${b.library}@${b.version}` : null);
      })
      .catch((e: unknown) => !off && setFailure(e instanceof ApiError ? e.code : "NET-SERVER"));
    return () => {
      off = true;
    };
  }, [api, onLatest]);

  useEffect(() => {
    if (secret) secretRef.current?.focus();
  }, [secret]);

  async function rotate(k: ServiceKey) {
    setBusy(true);
    setFailure(null);
    setCopied(false);
    try {
      setSecret(await api.rotateKey(k.keyId));
      await loadKeys();
    } catch (e) {
      setFailure(e instanceof ApiError ? e.code : "NET-SERVER");
    } finally {
      setBusy(false);
    }
  }

  async function revoke(k: ServiceKey) {
    if (confirm !== k.keyId) {
      setConfirm(k.keyId);
      return;
    }
    setBusy(true);
    setFailure(null);
    try {
      await api.revokeKey(k.keyId);
      setConfirm(null);
      await loadKeys();
    } catch (e) {
      setFailure(e instanceof ApiError ? e.code : "NET-SERVER");
    } finally {
      setBusy(false);
    }
  }

  const label = (group: string, v: string) =>
    hasMessage(`ops.${group}.${v}`) ? t(`ops.${group}.${v}`) : v;
  const chip = (s: string) =>
    s === "latest" || s === "active"
      ? "pf-chip-static--ok"
      : s === "pinned" || s === "expiring"
        ? "pf-chip-static--run"
        : "";

  return (
    <div className="pf-ops-assets" data-testid="ops-assets">
      <section className="pf-pane" aria-labelledby={`${id}-a`}>
        <h2 id={`${id}-a`} className="pf-h3">
          {t("ops.assets.title")}
        </h2>
        {assets === null ? (
          <p className="pf-muted">{t("common.loading")}</p>
        ) : assets.length === 0 ? (
          <Empty label={t("ops.assets.empty")} />
        ) : (
          <table className="pf-tbl" data-testid="asset-table">
            <thead>
              <tr>
                <th scope="col">{t("ops.assets.colLibrary")}</th>
                <th scope="col">{t("ops.assets.colVersion")}</th>
                <th scope="col">{t("ops.assets.colHash")}</th>
                <th scope="col">{t("ops.assets.colDate")}</th>
                <th scope="col">{t("ops.assets.colStatus")}</th>
              </tr>
            </thead>
            <tbody>
              {assets.map((a) => (
                <tr key={`${a.library}@${a.version}`}>
                  <th scope="row">
                    {a.library}
                    {a.key && a.key !== "*" && <span className="pf-muted"> / {a.key}</span>}
                  </th>
                  <td>
                    <b>{a.version}</b>
                  </td>
                  <td className="pf-muted" title={a.contentHash}>
                    <code>{shortHash(a.contentHash)}</code>
                  </td>
                  <td className="pf-muted">{fmtDate(a.createdAt)}</td>
                  <td>
                    <span className={`pf-chip-static ${chip(a.status)}`}>
                      {label("assets.status", a.status)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="pf-banner pf-banner--info" role="note">
          <p className="pf-banner__text">{t("ops.assets.note")}</p>
        </div>
      </section>

      <section className="pf-pane" aria-labelledby={`${id}-k`}>
        <h2 id={`${id}-k`} className="pf-h3">
          {t("ops.keys.title")}
        </h2>
        {secret && (
          <div
            ref={secretRef}
            tabIndex={-1}
            className="pf-banner pf-banner--warn pf-banner--list"
            role="alert"
            data-testid="key-secret"
          >
            <div className="pf-banner__body">
              <p className="pf-banner__text">
                <b>{t("ops.keys.secretTitle", { key: secret.keyId })}</b> {t("ops.keys.secretOnce")}
              </p>
              {secret.previousKeyId && (
                <p className="pf-banner__text" data-testid="key-overlap">
                  {t("ops.keys.overlap", {
                    prev: secret.previousKeyId,
                    at: fmtDateTime(secret.previousExpiresAt),
                  })}
                </p>
              )}
              <div className="pf-secret">
                <code data-testid="key-secret-value">{secret.secret}</code>
                <Button
                  variant="ghost"
                  className="pf-btn--sm"
                  onClick={() => {
                    void navigator.clipboard?.writeText(secret.secret).then(
                      () => setCopied(true),
                      () => setCopied(false),
                    );
                  }}
                >
                  {copied ? t("ops.keys.copied") : t("ops.keys.copy")}
                </Button>
                <Button
                  variant="accent"
                  className="pf-btn--sm"
                  data-testid="key-secret-done"
                  onClick={() => {
                    setSecret(null);
                    setCopied(false);
                  }}
                >
                  {t("ops.keys.secretDone")}
                </Button>
              </div>
            </div>
          </div>
        )}
        {keys === null ? (
          <p className="pf-muted">{t("common.loading")}</p>
        ) : (
          <table className="pf-tbl" data-testid="key-table">
            <thead>
              <tr>
                <th scope="col">{t("ops.keys.colId")}</th>
                <th scope="col">{t("ops.keys.colService")}</th>
                <th scope="col">{t("ops.keys.colCreated")}</th>
                <th scope="col">{t("ops.keys.colRotated")}</th>
                <th scope="col">{t("ops.keys.colExpires")}</th>
                <th scope="col">{t("ops.keys.colStatus")}</th>
                <th scope="col">
                  <span className="pf-sr-only">{t("teacher.nick.colAction")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => {
                const dead = k.status === "revoked";
                return (
                  <tr key={k.keyId} data-key={k.keyId}>
                    <th scope="row">{k.keyId}</th>
                    <td>{label("keys.service", k.service)}</td>
                    <td className="pf-muted">{fmtDate(k.createdAt)}</td>
                    <td className="pf-muted">{fmtDate(k.rotatedAt)}</td>
                    <td className="pf-muted">{fmtDate(k.expiresAt)}</td>
                    <td>
                      <span className={`pf-chip-static ${chip(k.status)}`}>
                        {label("keys.status", k.status)}
                      </span>
                      {k.successorKeyId && <span className="pf-muted"> → {k.successorKeyId}</span>}
                    </td>
                    <td className="pf-tbl__act">
                      {!dead && (
                        <div className="pf-row pf-tbl__actions">
                          {k.status === "active" && (
                            <Button
                              variant="ghost"
                              className="pf-btn--sm"
                              disabled={busy}
                              aria-label={t("ops.keys.rotateLabel", { key: k.keyId })}
                              data-testid="key-rotate"
                              onClick={() => void rotate(k)}
                            >
                              {t("ops.keys.rotate")}
                            </Button>
                          )}
                          <Button
                            variant={confirm === k.keyId ? "accent" : "ghost"}
                            className="pf-btn--sm"
                            disabled={busy}
                            aria-label={t(
                              confirm === k.keyId
                                ? "ops.keys.revokeConfirmLabel"
                                : "ops.keys.revokeLabel",
                              { key: k.keyId },
                            )}
                            data-testid="key-revoke"
                            onClick={() => void revoke(k)}
                          >
                            {confirm === k.keyId
                              ? t("ops.keys.revokeConfirm")
                              : t("ops.keys.revoke")}
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {failure && (
          <ErrorBanner
            cause={t("ops.common.saveFailed")}
            action={t("ops.common.retry")}
            code={failure}
          />
        )}
        <p className="pf-note">{t("ops.keys.note")}</p>
      </section>
    </div>
  );
}
